// optimized-animations.js

// ==========================
// Helpers
// ==========================

const initAndObserve = (triggerElement, elementsToAnimate, options) => {
  // Default options for each animation instance if not overridden in the specific call.
  // These are the defaults for the initAndObserve function itself.
  const {
    start = "top 80%",
    anim = {},
    stagger = 0.05, // More aggressive default stagger if individual functions don't set one
    duration = 0.5, // More aggressive default duration if individual functions don't set one
    ease = "sine.out"
  } = options;

  elementsToAnimate.forEach(el => {
    el.classList.add('scroll-animate-target');

    let initialX = "0px";
    let initialY = "0px";

    if (anim.x !== undefined) {
      initialX = `${anim.x}px`;
    }
    if (anim.y !== undefined) {
      initialY = `${anim.y}px`;
    }

    if (anim.x === undefined && anim.y === undefined) {
      if (triggerElement.matches('.reveal-children-left') || triggerElement.matches('.reveal-left') || triggerElement.matches('.reveal-first-level-left')) {
        initialX = '150px'; // Default for "left" classes: come from right
      } else if (triggerElement.matches('.reveal-children-right') || triggerElement.matches('.reveal-right') || triggerElement.matches('.reveal-first-level-right')) {
        initialX = '-150px'; // Default for "right" classes: come from left
      } else if (triggerElement.matches('.reveal-children-up') || triggerElement.matches('.reveal-up') || triggerElement.matches('.reveal-first-level-up')) {
        initialY = '150px'; // Default for "up" classes: come from bottom
      }
    }

    el.style.setProperty('--initial-x', initialX);
    el.style.setProperty('--initial-y', initialY);
    el.style.transitionDuration = `${duration}s`;
    el.style.transitionTimingFunction = (ease === "sine.inOut")
      ? 'cubic-bezier(0.445, 0.05, 0.55, 0.95)'
      : 'cubic-bezier(0.39, 0.575, 0.565, 1)';
  });

  let rootMargin = "0px 0px -20% 0px"; // Default for "top 80%"
  if (start === "top 90%") {
    rootMargin = "0px 0px -10% 0px";
  } else if (start === "top 100%") {
    rootMargin = "0px 0px 0px 0px";
  } else if (start.includes('%')) {
     const percentValue = parseInt(start.split(" ")[1], 10);
     if (!isNaN(percentValue)) {
        const offset = 100 - percentValue;
        rootMargin = `0px 0px -${offset}% 0px`;
     }
  }

  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        elementsToAnimate.forEach((el, index) => {
          el.style.transitionDelay = `${index * stagger}s`;
          el.classList.add('is-visible');
        });
        obs.unobserve(triggerElement);
      }
    });
  }, {
    root: null,
    rootMargin: rootMargin,
    threshold: 0.01
  });

  observer.observe(triggerElement);
};

const animateChildren = (selector, options = {}) => {
  document.querySelectorAll(selector).forEach(section => {
    const childrenToAnimate = Array.from(section.querySelectorAll("div, p, a, h1, h2, h3, h4, h5, h6, .wp-element-button"));
    if (childrenToAnimate.length > 0) {
      initAndObserve(section, childrenToAnimate, {
        // These are the defaults specific to animateChildren if not overridden in the call
        duration: 0.8,
        stagger: 0.5,
        ease: "sine.out",
        ...options, // User-provided options will override these defaults
      });
    }
  });
};

const animateSelf = (selector, options = {}) => {
  document.querySelectorAll(selector).forEach(section => {
    const childrenToAnimate = Array.from(section.children);
    if (childrenToAnimate.length > 0) {
      initAndObserve(section, childrenToAnimate, {
        // Defaults specific to animateSelf
        duration: 0.8,
        stagger: 0.5,
        ease: "sine.out",
        ...options,
      });
    }
  });
};

const animateFirstLevelChildren = (selector, options = {}) => {
  document.querySelectorAll(selector).forEach(section => {
    const childrenToAnimate = Array.from(section.children);
    if (childrenToAnimate.length > 0) {
      initAndObserve(section, childrenToAnimate, {
        // Defaults specific to animateFirstLevelChildren
        duration: 1.0, // Was 1s, can be overridden
        stagger: 0.5,  // Was 0.5s, can be overridden
        ease: "sine.inOut",
        ...options,
      });
    }
  });
};

// ==========================
// Reveal Animations
// ==========================
document.addEventListener('DOMContentLoaded', () => {
  console.log("DOM Content Loaded - Initializing Animations");

  // Default animations (no specific anim options, relies on trigger class for direction)
  //animateFirstLevelChildren([".reveal-children", ".site-content", ".reveal-children-up", "[data-section-container]"].join(','));
    // If .reveal-children-left is typically used for column layouts,
  // consider using animateFirstLevelChildren and making it faster.
  animateChildren([".reveal-children", ".site-content", ".reveal-children-up", "[data-section-container]"], {
    start: "top 90%",   // Or your preferred start trigger point
    anim: { y: 150 },     // To slide in from the RIGHT. Use x: -150 to slide from LEFT.
    duration: 0.6,      // FASTER: Each column takes 0.6s to animate
    stagger: 0.2        // FASTER: Next column starts 0.2s after the previous
  });


  // --- MODIFICATION FOR .reveal-children-left ---
  // If .reveal-children-left is typically used for column layouts,
  // consider using animateFirstLevelChildren and making it faster.
  animateFirstLevelChildren(".reveal-children-left", {
    start: "top 90%",   // Or your preferred start trigger point
    anim: { x: 150 },     // To slide in from the RIGHT. Use x: -150 to slide from LEFT.
    duration: 0.6,      // FASTER: Each column takes 0.6s to animate
    stagger: 0.2        // FASTER: Next column starts 0.2s after the previous
  });

  // For .reveal-children-right, if it's also columns, apply similar logic:
  animateFirstLevelChildren(".reveal-children-right", {
    anim: { x: -150 },    // To slide in from the LEFT.
    duration: 0.6,
    stagger: 0.2
  });
  // If .reveal-children-right is NOT for columns and you want to animate all descendants,
  // you might stick with animateChildren but adjust its speed:
  // animateChildren(".reveal-children-right", { anim: { x: -150 }, duration: 0.6, stagger: 0.2 });


  animateSelf(".reveal-left", { anim: { x: 150 } }); // Consider adjusting duration/stagger if needed
  animateSelf(".reveal-up", { anim: { y: 150 } });   // Consider adjusting duration/stagger if needed
  animateSelf(".reveal-right", { anim: { x: -150 } });// Consider adjusting duration/stagger if needed

  animateFirstLevelChildren(".reveal-first-level-up", { anim: { y: 150 } }); // Default duration 1s, stagger 0.5s
  animateFirstLevelChildren(".reveal-first-level-left", { anim: { x: 150 } }); // Default duration 1s, stagger 0.5s
  animateFirstLevelChildren(".reveal-first-level-right", { anim: { x: -150 } });// Default duration 1s, stagger 0.5s

  // Initialize Marquee
  initInfiniteMarquee() // Ensure this is called after DOM is ready

});

const setupMarquee = (marqueeContainer, index) => {
            const marqueeContent = marqueeContainer.querySelector('.marquee-content');
            const direction = marqueeContainer.dataset.direction || 'left';
            const originalItems = Array.from(marqueeContent.children);

            // 1. Fill the container: Keep cloning the original items until the content's width
            // is greater than the container's width. This ensures the initial block (Block A) is large enough.
            while (marqueeContent.offsetWidth < marqueeContainer.offsetWidth) {
                originalItems.forEach(item => {
                    marqueeContent.appendChild(item.cloneNode(true));
                });
            }

            // 2. Create the loop: Clone all the current items (the now-filled Block A) to create
            // an identical Block B. The structure is now [A][B], which is essential for a seamless loop.
            const allCurrentItems = Array.from(marqueeContent.children);
            allCurrentItems.forEach(item => {
                marqueeContent.appendChild(item.cloneNode(true));
            });

            // 3. Create and inject the dynamic animation
            const loopWidth = marqueeContent.offsetWidth / 2;
            const animationDuration = loopWidth / 20; // Adjust speed by changing the divisor
            const animationName = `scrollMarquee-${index}`;

            let fromTransform, toTransform;
            if (direction === 'right') {
                // For a right-scrolling marquee, we must set its initial position
                // to the start of the second block to prevent a visual jump.
                marqueeContent.style.transform = `translateX(-${loopWidth}px)`;
                
                fromTransform = `translateX(-${loopWidth}px)`;
                toTransform = `translateX(0)`;
            } else { // 'left' or default
                fromTransform = `translateX(0)`;
                toTransform = `translateX(-${loopWidth}px)`;
            }

            const styleSheet = document.createElement("style");
            styleSheet.innerHTML = `
                @keyframes ${animationName} {
                    from { transform: ${fromTransform}; }
                    to { transform: ${toTransform}; }
                }
            `;
            document.head.appendChild(styleSheet);

            // Apply the dynamic animation
            marqueeContent.style.animation = `${animationName} ${animationDuration}s linear infinite`;
        }

        /**
         * Initializes all marquees on the page. It waits for images inside each marquee
         * to load before setting it up, preventing calculation errors.
         */
        const initInfiniteMarquees = () => {
            const marqueeContainers = document.querySelectorAll('.marquee-container');
            marqueeContainers.forEach((container, index) => {
                const images = Array.from(container.querySelectorAll('img'));
                const allImagesLoaded = images.map(img => {
                    return new Promise(resolve => {
                        if (img.complete) {
                            resolve();
                        } else {
                            img.onload = resolve;
                            img.onerror = resolve; // Resolve even on error to not hang the script
                        }
                    });
                });

                // Once all images for THIS specific marquee are loaded, set it up.
                Promise.all(allImagesLoaded).then(() => {
                    setupMarquee(container, index);
                });
            });
        };

        // Run the initialization function after the page has loaded.
        window.addEventListener('load', initInfiniteMarquees);

